/* global organisationId indexUrl eventsUrl exportUrl savedSetsUrl isOrganisationAdmin */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

import App from 'App';

import ShowInSelect, { EVENT_CURRENCY, FxMissingAlert, readFxOptions } from '../ShowInSelect';
import submitExport, { selectionFields } from '../submitExport';

import ByProjectTab, { LAYOUT_COLUMNS, LAYOUT_ROWS } from './by_project/ByProjectTab';
import CashflowKpis from './CashflowKpis';
import CashflowTable from './CashflowTable';
import FilterBar from './filter/FilterBar';
import SelectedProjectsStrip from './filter/SelectedProjectsStrip';
import FinancialTable from './FinancialTable';
import useSummaryFilters, { PRESET_LIFETIME, TAB_BY_PERIOD, TAB_BY_PROJECT, TAB_CASHFLOW } from './hooks/useSummaryFilters';
import KpiTiles from './KpiTiles';
import OrganisationCashflowChart from './OrganisationCashflowChart';
import ProjectPicker from './picker/ProjectPicker';
import SummaryHeader from './SummaryHeader';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import Tabs, { RenderStrategyOnFirstVisit } from 'CommonComponents/Tabs';

import { ConfirmationModalProvider } from 'Context/ConfirmationModalContext';

import FileExportIcon from 'Assets/icons/file-export.svg';

/** A state updater that adds the key to a set, or takes it out if present. */
const toggleMember = (key) => (previous) => {
    const next = new Set(previous);
    if (next.has(key)) {
        next.delete(key);
    } else {
        next.add(key);
    }

    return next;
};

/** Every key in the tree that has children to open. */
const expandableKeys = (rows) => rows.filter((row) => row.children?.length > 0).map((row) => row.key);

export default function OrganisationSummaryIndex(props) {
    const [request, setRequest] = useState(false);
    const [data, setData] = useState(null);
    const [events, setEvents] = useState([]);
    const [presets, setPresets] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [withArchived, setWithArchived] = useState(false);

    const [layout, setLayout] = useState(LAYOUT_ROWS);
    const [committedActuals, setCommittedActuals] = useState(false);
    const [expanded, setExpanded] = useState(() => new Set());
    const [pivotExpanded, setPivotExpanded] = useState(() => new Set());
    const [periodExpanded, setPeriodExpanded] = useState(() => new Set());
    const [showIn, setShowIn] = useState(EVENT_CURRENCY);
    const fxAvailable = !!props.fxOptions?.anchor;

    const {
        hydrated,
        filterData,
        dateRange,
        granularity,
        activeTab,
        selectedEvents,
        selectedCount,
        currencyStatus,
        removeProject,
        setSelection,
        setRange,
        setLifetime,
        setGranularity,
        setActiveTab,
    } = useSummaryFilters(props.organisationId, events);

    const readSeq = useRef(0);

    useEffect(() => {
        const url = new URL(props.eventsUrl);
        if (withArchived) {
            url.searchParams.set('with_archived', '1');
        }

        axios
            .get(url.toString())
            .then((res) => {
                setEvents(res.data.events || []);
                setPresets(res.data.presets || null);
            })
            .catch((e) => {
                console.log(e);
            });
    }, [withArchived]);

    // Recalc whenever any filter dimension changes (after hydration). With no
    // project selected the UI shows the "Select projects to begin" empty state,
    // so skip the (heavy) POST and clear any stale data the empty state replaces.
    useEffect(() => {
        if (!hydrated) {
            return;
        }

        if (selectedCount === 0) {
            readSeq.current++;
            setData(null);
            setRequest(false);

            return;
        }

        const sequence = ++readSeq.current;
        setRequest(true);

        axios
            .post(props.indexUrl, {
                filters: filterData,
                from: dateRange.from,
                to: dateRange.to,
                granularity,
                committed_actuals: committedActuals,
                show_in: showIn,
            })
            .then((res) => {
                // Only the newest read may land: the controls stay live while one
                // is in flight.
                if (sequence === readSeq.current) {
                    setData(res.data);
                }
            })
            .catch((er) => {
                console.log(er);
            })
            .finally(() => {
                if (sequence === readSeq.current) {
                    setRequest(false);
                }
            });
    }, [hydrated, filterData, selectedCount, dateRange.from, dateRange.to, granularity, committedActuals, showIn]);

    const applyPickerSelection = (draft) => {
        setSelection(draft);
        setPickerOpen(false);
    };

    const onApplyPreset = (presetId, range) => {
        if (range) {
            setRange(range.from, range.to, presetId);
        }
    };

    const report = data?.by_project || null;
    const projectKeys = useMemo(() => expandableKeys(report?.projects || []), [report]);
    const pivotKeys = useMemo(() => {
        const keys = [];
        const walk = (rows) =>
            rows.forEach((row) => {
                if (row.children.length > 0) {
                    keys.push(row.key);
                    walk(row.children);
                }
            });
        walk([...(report?.pivot?.revenue || []), ...(report?.pivot?.expenditure || [])]);

        return keys;
    }, [report]);

    // "Expand departments" opens every project to its departments in the rows
    // layout, and every category and department to its cost centres in the
    // columns layout.
    const isColumns = layout === LAYOUT_COLUMNS;
    const layoutKeys = isColumns ? pivotKeys : projectKeys;
    const layoutExpanded = isColumns ? pivotExpanded : expanded;
    const setLayoutExpanded = isColumns ? setPivotExpanded : setExpanded;
    const allExpanded = layoutKeys.length > 0 && layoutKeys.every((key) => layoutExpanded.has(key));

    const expandAll = (checked) =>
        setLayoutExpanded((previous) => {
            const next = new Set(previous);
            layoutKeys.forEach((key) => (checked ? next.add(key) : next.delete(key)));

            return next;
        });

    const toggleExpanded = useCallback((key) => setLayoutExpanded(toggleMember(key)), [setLayoutExpanded]);
    const togglePeriod = useCallback((key) => setPeriodExpanded(toggleMember(key)), []);

    const exportReport = () => {
        const fields = [
            ['granularity', granularity],
            ['tab', activeTab],
            ['layout', layout],
            ['committed_actuals', committedActuals ? '1' : '0'],
            ['show_in', showIn],
            ...selectionFields(filterData),
        ];

        if (dateRange.from && dateRange.to) {
            fields.push(['from', dateRange.from], ['to', dateRange.to]);
        }

        const open = { [TAB_BY_PROJECT]: layoutExpanded, [TAB_BY_PERIOD]: periodExpanded }[activeTab] || new Set();
        open.forEach((key) => fields.push(['expanded[]', key]));

        submitExport(props.exportUrl, fields);
    };

    const columns = data?.columns || [];
    // Present only while the figures are shown in a chosen currency.
    const fx = data?.fx || null;
    const monthlyNet = data?.cashflow?.monthly_net || [];

    const tabs = [
        {
            id: TAB_BY_PROJECT,
            title: $t('By project'),
            content: () => (
                <ByProjectTab
                    report={report}
                    fx={fx}
                    fxAvailable={fxAvailable}
                    loading={request}
                    layout={layout}
                    onChangeLayout={setLayout}
                    committedActuals={committedActuals}
                    onChangeCommittedActuals={setCommittedActuals}
                    expanded={layoutExpanded}
                    onToggle={toggleExpanded}
                    allExpanded={allExpanded}
                    canExpand={layoutKeys.length > 0}
                    onExpandAll={expandAll}
                />
            ),
        },
        {
            id: TAB_BY_PERIOD,
            title: $t('By period'),
            content: () =>
                data?.budget_summary && (
                    <FinancialTable
                        data={data.budget_summary}
                        columns={columns}
                        mixedCurrencies={currencyStatus.mixed && !fx}
                        fx={fx}
                        expanded={periodExpanded}
                        onToggle={togglePeriod}
                    />
                ),
        },
        {
            id: TAB_CASHFLOW,
            title: $t('Cashflow'),
            content: () => (
                <div>
                    <CashflowKpis
                        loading={request && !data?.cashflow_kpis}
                        cashflowKpis={data?.cashflow_kpis || null}
                        monthlyNet={monthlyNet}
                        selectedCount={selectedCount}
                        onExtendRange={dateRange.presetId !== PRESET_LIFETIME ? setLifetime : null}
                    />
                    <OrganisationCashflowChart monthlyNet={monthlyNet} cashflowKpis={data?.cashflow_kpis || null} />
                    {data?.cashflow && <CashflowTable data={data.cashflow} columns={columns} fx={fx} />}
                </div>
            ),
        },
    ];

    const actions = [
        {
            content: () => (
                <Button variant="primary" onClick={exportReport} disabled={!data}>
                    <FileExportIcon className="tw-w-3 tw-h-3" />
                    {$t('Export to excel')}
                </Button>
            ),
        },
    ];

    const hasSelection = selectedCount > 0;
    const kpis = data?.kpis || null;

    return (
        <div className="tw-space-y-4">
            <div className="card bg-white border-0 rounded-15px shadow">
                <div className="card-body tw-space-y-4">
                    <SummaryHeader />

                    <FilterBar
                        events={events}
                        selectedCount={selectedCount}
                        dateRange={dateRange}
                        coveredRange={data?.range || null}
                        granularity={granularity}
                        presets={presets}
                        onOpenPicker={() => setPickerOpen(true)}
                        onApplyPreset={onApplyPreset}
                        onApplyLifetime={setLifetime}
                        onApplyCustom={(from, to) => setRange(from, to)}
                        onChangeGranularity={setGranularity}
                    />

                    {pickerOpen && (
                        <ProjectPicker
                            events={events}
                            filterData={filterData}
                            isAdmin={!!props.isOrganisationAdmin}
                            savedSetsUrl={props.savedSetsUrl}
                            showArchived={withArchived}
                            onToggleArchived={setWithArchived}
                            onApply={applyPickerSelection}
                            onClose={() => setPickerOpen(false)}
                        />
                    )}

                    {/* Without an FX anchor the page is exactly as it was: no Show in. */}
                    {fxAvailable ? (
                        <div className="tw-flex tw-flex-wrap tw-items-end tw-justify-between tw-gap-4">
                            <div className="tw-min-w-0 tw-flex-1">
                                <SelectedProjectsStrip selectedEvents={selectedEvents} onRemove={removeProject} showCurrency limit={null} />
                            </div>
                            <ShowInSelect fxOptions={props.fxOptions} value={showIn} onChange={setShowIn} />
                        </div>
                    ) : (
                        <SelectedProjectsStrip selectedEvents={selectedEvents} onRemove={removeProject} showCurrency limit={null} />
                    )}
                </div>
            </div>

            <FxMissingAlert fx={fx} fxUrl={props.fxOptions?.fx_url} />

            {currencyStatus.mixed && showIn === EVENT_CURRENCY && (
                <Alert type="warning">{$t('Selected projects use more than one currency. Totals below are shown without currency conversion.')}</Alert>
            )}

            <KpiTiles loading={request && !kpis} kpis={kpis} selectedCount={selectedCount} committedActuals={committedActuals} fx={fx} />

            {hasSelection ? (
                <div className="table__card">
                    <Tabs tabs={tabs} actions={actions} activeTab={activeTab} onTabActivate={setActiveTab} renderStrategy={RenderStrategyOnFirstVisit} />
                </div>
            ) : (
                <div className="tw-px-4 tw-py-8 tw-text-center tw-text-sm tw-text-charcoal-50 tw-rounded-lg tw-border-2 tw-border-dashed tw-border-grey">
                    {$t('Select projects to begin')}
                </div>
            )}
        </div>
    );
}

if (document.getElementById('organisationSummary')) {
    var rootEl = document.getElementById('organisationSummary');
    ReactDOM.render(
        <App>
            <ConfirmationModalProvider>
                <OrganisationSummaryIndex
                    organisationId={organisationId}
                    indexUrl={indexUrl}
                    eventsUrl={eventsUrl}
                    exportUrl={exportUrl}
                    savedSetsUrl={savedSetsUrl}
                    isOrganisationAdmin={isOrganisationAdmin}
                    fxOptions={readFxOptions(rootEl)}
                />
            </ConfirmationModalProvider>
        </App>,
        rootEl,
    );
}
